import React, { useState } from 'react';
import './MedicationComponent.css';
import SkipMissedModal from './SkipMissedModal';

const MedicationComponent = ({ 
  medicationId,
  medicationName = "Medication",
  totalDosage, // Now optional - no default
  quantityToTake = 1,
  unit = "dose",
  instructions, // Now optional - no default
  imageUrl = "/defaultpill.png",
  status = "pending",
  onStatusChange,
  isAsNeeded = false, // New prop to control button display
  takenCount = 0
}) => {
  
  // Added state for modal
  const [showModal, setShowModal] = useState(false);

  const handleTake = () => {
    // For as-needed: toggle between taken and pending
    // For scheduled: mark as taken
    if (isAsNeeded && status === 'taken') {
      onStatusChange(medicationId, 'pending');
    } else {
      onStatusChange(medicationId, 'taken');
    }
  };

  const handleSkipClick = () => {
    // Open modal instead of directly skipping
    setShowModal(true);
  };

  const handleModalSelection = (selectedStatus) => {
    onStatusChange(medicationId, selectedStatus);
  };

  const handleUntake = () => {
    onStatusChange(medicationId, 'pending');
  };

  // Build the medication title - only include dosage if it exists
  const getMedicationTitle = () => {
    if (totalDosage) {
      return `${medicationName} - ${totalDosage}`;
    }
    return medicationName;
  };

  // Build the quantity text - handle pluralization
  const getQuantityText = () => {
    if (quantityToTake === 1) {
      return `Take ${quantityToTake} ${unit}`;
    }
    // Handle plural forms
    const pluralUnit = unit.endsWith('s') ? unit : `${unit}s`;
    return `Take ${quantityToTake} ${pluralUnit}`;
  };

  return (
    <div className="medication-component">
      {/* Section 1: Medication Image */}
      {/* <div className="medication-image">
        <img 
          src={imageUrl} 
          alt={`${medicationName} medication`}
          className="med-image"
          onError={(e) => {
            // Fallback to default image if image fails to load
            e.target.src = '/defaultpill.png';
          }}
        />
      </div>*/}

      {/* Section 2: Medication Info */}
      <div className="medication-info">
        <h3 className="medication-name">
          {getMedicationTitle()}
        </h3>
        <p className="dosage-info">
          {getQuantityText()}
        </p>
        {/* Only show instructions if they exist */}
        {instructions && (
          <p className="instructions">
            <span className="instructions-icon">📝</span> {instructions}
          </p>
        )}
      </div>
      
      {/* Section 3: CTAs/Buttons */}
      <div className="action-buttons">

      {isAsNeeded ? (
        // As-needed medications show taken button + count
        <div className="as-needed-actions">
          <button 
            className={`icon-button ${status === 'taken' ? 'taken' : 'taken-option'}`}
            onClick={handleTake}
            aria-label={status === 'taken' ? 'Taken - tap to undo' : 'Mark as taken'}
          >
            ✓
          </button>
          {takenCount > 0 && (
            <div className="taken-count-label">
              Taken {takenCount} time{takenCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      ) : (

          // Scheduled medications show both buttons or status
          <>
            {status === 'pending' && (
              <>
                <button 
                  className="icon-button taken-option"
                  onClick={handleTake}
                  aria-label="Mark as taken"
                >
                  ✓
                </button>
                
                <button 
                  className="icon-button skipped-option"
                  onClick={handleSkipClick} // Opens modal
                  aria-label="Mark as Skip or Missed"
                >
                  ✕
                </button>
              </>
            )}
            
            {status === 'taken' && (
              <button 
                className="icon-button taken"
                onClick={handleUntake}
                aria-label="Taken - tap to undo"
              >
                ✓
              </button>
            )}
            
            {status === 'skipped' && (
              <button 
                className="icon-button skipped"
                onClick={handleUntake}
                aria-label="Skipped - tap to undo"
              >
                &gt;
              </button>
            )}

            {status === 'missed' && (
              <button
                className="icon-button missed"
                onClick={handleUntake}
                aria-label="Missed - tap to undo"
              >
                ✕
              </button>
            )}
          </>
        )}
      </div>
      
      {/* Modal for Skip/Missed selection */}
      {showModal && (
        <SkipMissedModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSelectStatus={handleModalSelection}
          medicationName={getMedicationTitle()}
        />
      )}
    </div>
  );
};

export default MedicationComponent;